import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import * as LucideIcons from 'lucide-react';
import {
  Play, Download, FileText, ArrowRight, Calendar,
  Headphones, MessageCircle, LayoutGrid,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useContactInfo, getWhatsAppHref } from '../contexts/ContactInfoContext';
import {
  MEDIA_VIDEOS, MEDIA_POSTERS, MEDIA_PDFS, MEDIA_FILTERS, filterMedia,
} from '../data/mediaHubContent';

/**
 * VietClinics Media Hub — premium central library of videos, posters,
 * PDFs, clinic visits, patient guides and campaigns.
 *
 * Data lives in `src/data/mediaHubContent.js`. The component is filter-aware
 * (chip nav above the content) and ready to be wired to a CMS later — only
 * the import source needs to change.
 */

const resolveIcon = (name) => (name && LucideIcons[name]) || LayoutGrid;

const MediaHubPage = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const { contactInfo } = useContactInfo();
  const waLink = getWhatsAppHref(
    contactInfo,
    'Hi VietClinics! I have a question about your media / resources.',
  );

  // Derive what to show per section, based on the active filter.
  const videos = useMemo(() => filterMedia(MEDIA_VIDEOS, activeFilter), [activeFilter]);
  const posters = useMemo(() => filterMedia(MEDIA_POSTERS, activeFilter), [activeFilter]);
  const pdfs = useMemo(() => filterMedia(MEDIA_PDFS, activeFilter), [activeFilter]);

  const showVideos = activeFilter === 'all' || activeFilter === 'video' || activeFilter === 'clinic-visit';
  const showPosters = activeFilter === 'all' || activeFilter === 'poster' || activeFilter === 'campaign';
  const showPdfs = activeFilter === 'all' || activeFilter === 'pdf' || activeFilter === 'patient-guide';

  const canonical = typeof window !== 'undefined'
    ? `${window.location.origin}/media`
    : 'https://vietclinics.com/media';

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>VietClinics Media Hub — Videos, Guides & Campaigns</title>
        <meta name="description" content="Explore VietClinics videos, clinic walkthroughs, patient guides, campaign materials and downloadable PDFs — your central healthcare media library." />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="VietClinics Media Hub" />
        <meta property="og:description" content="Videos, guides, campaigns and downloadable resources from VietClinics Vietnam." />
        <meta property="og:type" content="website" />
      </Helmet>

      <Header />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-[#F0FAF8]" data-testid="media-hero">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(180deg, #F0FAF8 0%, #FAFCFB 100%)' }}
        />
        <div className="relative z-10 container mx-auto px-4 max-w-7xl py-12 lg:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-6 flex flex-col gap-5">
              <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-[0.22em]">
                Media Hub
              </p>
              <h1
                className="font-extrabold tracking-tight text-slate-900 leading-[1.05]"
                style={{ fontSize: 'clamp(2.4rem, 5vw, 3.8rem)', letterSpacing: '-0.02em' }}
              >
                VietClinics
                <br />
                Media Hub
              </h1>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed max-w-lg">
                Explore videos, clinic updates, patient guides, campaign
                materials, and healthcare content from VietClinics Vietnam.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <a
                  href="#featured-videos"
                  className="inline-flex items-center gap-2 text-white font-bold rounded-2xl px-6 py-3.5 transition-all hover:-translate-y-0.5"
                  style={{
                    background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)',
                    boxShadow: '0 12px 24px rgba(31,167,161,0.25)',
                  }}
                  data-testid="media-cta-view-videos"
                >
                  <Play className="h-4 w-4" />
                  View Videos
                </a>
                <a
                  href="#pdf-guides"
                  className="inline-flex items-center gap-2 bg-white text-[#1FA7A1] font-bold rounded-2xl px-6 py-3.5 border-2 border-[#1FA7A1] hover:bg-[#1FA7A1]/5 transition-all"
                  data-testid="media-cta-download-guides"
                >
                  <Download className="h-4 w-4" />
                  Download Guides
                </a>
              </div>
            </div>

            <div className="lg:col-span-6 relative">
              <div
                className="relative rounded-[24px] overflow-hidden aspect-[16/11]"
                style={{ boxShadow: '0 24px 60px rgba(15,42,85,0.18)' }}
              >
                <img
                  src="https://images.pexels.com/photos/4226140/pexels-photo-4226140.jpeg?auto=compress&cs=tinysrgb&w=1400"
                  alt="VietClinics Media Hub"
                  className="w-full h-full object-cover"
                  loading="eager"
                  onError={(e) => { e.target.src = 'https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&w=1400'; }}
                />
                <div
                  aria-hidden
                  className="absolute inset-0 pointer-events-none"
                  style={{ background: 'linear-gradient(135deg, rgba(240,250,248,0.20) 0%, transparent 60%)' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FILTER CHIPS ─────────────────────────────────────────────────── */}
      <section className="bg-white border-y border-slate-100 sticky top-0 z-20" data-testid="media-filter-bar">
        <div className="container mx-auto px-4 max-w-7xl py-4">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1">
            {MEDIA_FILTERS.map((f) => {
              const Icon = resolveIcon(f.icon);
              const active = activeFilter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setActiveFilter(f.key)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold whitespace-nowrap transition-all flex-shrink-0 ${
                    active
                      ? 'text-white shadow-[0_8px_18px_rgba(31,167,161,0.25)]'
                      : 'bg-white text-slate-600 border border-slate-200 hover:border-[#1FA7A1] hover:text-[#1FA7A1]'
                  }`}
                  style={active ? { background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)' } : undefined}
                  data-testid={`media-filter-${f.key}`}
                  aria-pressed={active}
                >
                  <Icon className="h-4 w-4" />
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FEATURED VIDEOS ──────────────────────────────────────────────── */}
      {showVideos && videos.length > 0 && (
        <section id="featured-videos" className="py-16 lg:py-20 bg-white" data-testid="media-videos-section">
          <div className="container mx-auto px-4 max-w-7xl">
            <SectionHeader title="Featured Videos" seeAllLabel="View all videos" seeAllHref="#featured-videos" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
              {videos.map((v) => <VideoCard key={v.id} item={v} />)}
            </div>
          </div>
        </section>
      )}

      {/* ── POSTERS & CAMPAIGNS ──────────────────────────────────────────── */}
      {showPosters && posters.length > 0 && (
        <section className="py-16 lg:py-20 bg-[#F8FAFC]" data-testid="media-posters-section">
          <div className="container mx-auto px-4 max-w-7xl">
            <SectionHeader title="Posters & Campaigns" seeAllLabel="View all posters" seeAllHref="#" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
              {posters.map((p) => <PosterCard key={p.id} item={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* ── PDF & GUIDES ─────────────────────────────────────────────────── */}
      {showPdfs && pdfs.length > 0 && (
        <section id="pdf-guides" className="py-16 lg:py-20 bg-white" data-testid="media-pdfs-section">
          <div className="container mx-auto px-4 max-w-7xl">
            <SectionHeader title="PDF & Guides" seeAllLabel="View all PDFs" seeAllHref="#pdf-guides" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
              {pdfs.map((p) => <PdfCard key={p.id} item={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* Empty state (filter has no matches across all sections) */}
      {!showVideos && !showPosters && !showPdfs && (
        <section className="py-20 bg-white text-center text-slate-500">
          No media for this filter yet — check back soon.
        </section>
      )}

      {/* ── BOTTOM CTA ───────────────────────────────────────────────────── */}
      <section className="py-12 lg:py-16 bg-white" data-testid="media-bottom-cta">
        <div className="container mx-auto px-4 max-w-6xl">
          <div
            className="rounded-[24px] flex flex-col lg:flex-row items-center justify-between gap-6 p-7 lg:p-9"
            style={{ background: 'linear-gradient(135deg, #E6F6F4 0%, #DCEFEC 100%)' }}
          >
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                <Headphones className="h-6 w-6 text-[#1FA7A1]" />
              </div>
              <div>
                <h3 className="font-extrabold text-slate-900 text-xl tracking-tight">Need more information?</h3>
                <p className="text-slate-600 text-sm lg:text-base mt-1">
                  Our care team is here to help you with any questions.
                </p>
              </div>
            </div>
            {waLink ? (
              <a
                href={waLink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-white font-bold rounded-full px-7 py-3.5 transition-all hover:-translate-y-0.5"
                style={{
                  background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)',
                  boxShadow: '0 12px 24px rgba(31,167,161,0.25)',
                }}
                data-testid="media-talk-cta"
              >
                <MessageCircle className="h-4 w-4" />
                Talk to VietClinics
              </a>
            ) : (
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-white font-bold rounded-full px-7 py-3.5 transition-all hover:-translate-y-0.5"
                style={{
                  background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)',
                  boxShadow: '0 12px 24px rgba(31,167,161,0.25)',
                }}
                data-testid="media-talk-cta"
              >
                <MessageCircle className="h-4 w-4" />
                Talk to VietClinics
              </Link>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

/* ───────────────────────────────────────────────────────────────────────── */
/* Sub-components                                                            */
/* ───────────────────────────────────────────────────────────────────────── */

const SectionHeader = ({ title, seeAllLabel, seeAllHref }) => (
  <div className="flex items-end justify-between gap-4 mb-8">
    <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
      {title}
    </h2>
    {seeAllHref && (
      <a
        href={seeAllHref}
        className="hidden sm:inline-flex items-center gap-1.5 text-sm font-bold text-[#1FA7A1] hover:gap-2 transition-all whitespace-nowrap"
      >
        {seeAllLabel}
        <ArrowRight className="h-4 w-4" />
      </a>
    )}
  </div>
);

const VideoCard = ({ item }) => (
  <a
    href={item.url}
    target={item.url && item.url !== '#' ? '_blank' : undefined}
    rel="noreferrer"
    className="group block bg-white rounded-2xl border border-slate-100 overflow-hidden hover:-translate-y-1 transition-all duration-300"
    style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.05)' }}
    data-testid={`media-video-card-${item.id}`}
  >
    <div className="relative aspect-video overflow-hidden bg-slate-100">
      <img
        src={item.thumbnail}
        alt={item.title}
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(180deg, transparent 50%, rgba(15,42,85,0.30) 100%)' }}
      />
      {/* Play badge */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="w-14 h-14 rounded-full bg-white/85 backdrop-blur-md flex items-center justify-center group-hover:scale-110 transition-transform"
          style={{ boxShadow: '0 8px 22px rgba(15,42,85,0.18)' }}
        >
          <Play className="h-5 w-5 text-[#1FA7A1] fill-[#1FA7A1] ml-0.5" />
        </div>
      </div>
      {/* Duration pill */}
      <span className="absolute bottom-2.5 right-2.5 text-[11px] font-bold text-white bg-slate-900/75 backdrop-blur-sm px-2 py-1 rounded-md">
        {item.duration}
      </span>
    </div>
    <div className="p-5">
      <h3 className="font-extrabold text-slate-900 text-base leading-snug mb-2 line-clamp-2">{item.title}</h3>
      <p className="text-xs font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">{item.tag}</p>
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        <Calendar className="h-3.5 w-3.5" />
        {item.date}
      </div>
    </div>
  </a>
);

const PosterCard = ({ item }) => (
  <div
    className="group block bg-white rounded-2xl border border-slate-100 overflow-hidden hover:-translate-y-1 transition-all duration-300"
    style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.05)' }}
    data-testid={`media-poster-card-${item.id}`}
  >
    <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
      <img
        src={item.image}
        alt={item.title}
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-90"
        style={{ background: `linear-gradient(135deg, ${item.accent || '#1FA7A1'}30 0%, transparent 60%)` }}
      />
    </div>
    <div className="p-5">
      <h3 className="font-extrabold text-slate-900 text-base leading-snug mb-3">{item.title}</h3>
      <span
        className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest"
        style={{ background: '#DCEFEC', color: '#1A8F8A' }}
      >
        {item.tag}
      </span>
    </div>
  </div>
);

const PdfCard = ({ item }) => (
  <a
    href={item.url}
    target={item.url && item.url !== '#' ? '_blank' : undefined}
    rel="noreferrer"
    className="group block bg-white rounded-2xl border border-slate-100 p-5 hover:-translate-y-1 hover:border-[#1FA7A1] transition-all duration-300"
    style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.05)' }}
    data-testid={`media-pdf-card-${item.id}`}
  >
    <div className="flex items-center gap-2 mb-4">
      <div
        className="w-9 h-10 rounded-md flex items-center justify-center text-[10px] font-extrabold text-white"
        style={{ background: '#E84A3C' }}
      >
        PDF
      </div>
      <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">PDF</span>
    </div>
    <h3 className="font-extrabold text-slate-900 text-[15px] leading-snug mb-2 line-clamp-2 min-h-[2.4em]">
      {item.title}
    </h3>
    {item.description && (
      <p className="text-xs text-slate-500 leading-relaxed mb-4 line-clamp-2">{item.description}</p>
    )}
    <div className="text-xs text-slate-400 mb-3">{item.size}</div>
    <div className="inline-flex items-center gap-1.5 text-sm font-bold text-[#1FA7A1] group-hover:gap-2 transition-all">
      <Download className="h-4 w-4" />
      Download
    </div>
  </a>
);

export default MediaHubPage;
